import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/company/companyAdd.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCompany, createCompanyWarehouse, warehouseList, getCountries, getStates, getCities } from '../../services/Api';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import { AiOutlineLoading } from 'react-icons/ai';

const CompanyAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('company');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [companyId, setCompanyId] = useState(null);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseData, setWarehouseData] = useState([{
        warehouse_id: '',
        membership_till: '',
        lot_min: '',
        lot_max: '',
        is_primary: true
    }]);

    // Company form data
    const [companyData, setCompanyData] = useState({
        name: '',
        uen_number: '',
        contact_number: '',
        email: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        remark: '',
        contact_person_phone: ''
    });

    // const handleTabChange = (tab) => {
    //     setActiveTab(tab);
    // };

    const handleTabChange = (tab) => {
        if (tab === 'warehouse' && !companyId) {
            showPopup('warning', 'Warning', 'Please complete company details first');
            return;
        }
        setActiveTab(tab);
    };

    useEffect(() => {
        console.log('CompanyId state changed:', companyId);
    }, [companyId]);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseList();
                setWarehouses(response.data);
            } catch (error) {
                console.error("Error fetching warehouses:", error);
                showPopup('error', 'Error', 'Failed to load warehouses');
            }
        };

        const fetchCountries = async () => {
            setLoadingLocations(prev => ({ ...prev, countries: true }));
            try {
                const response = await getCountries();
                // Extract actual countries array from response.data.data
                if (response.data && Array.isArray(response.data.data)) {
                    setCountries(response.data.data);
                } else {
                    console.error("Expected an array for countries, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load countries: Invalid data format');
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
                showPopup('error', 'Error', 'Failed to load countries');
            } finally {
                setLoadingLocations(prev => ({ ...prev, countries: false }));
            }
        };

        fetchWarehouses();
        fetchCountries();

        if (location.state?.barcode) {
            setCompanyData(prev => ({
                ...prev,
                barcode_number: location.state.barcode
            }));
        }
    }, [location.state]);

    // Fetch states when country changes
    useEffect(() => {
        const fetchStates = async () => {
            if (!companyData.country_id) {
                setStates([]);
                return;
            }
            setLoadingLocations(prev => ({ ...prev, states: true }));
            try {
                const response = await getStates(companyData.country_id);
                // Check if response.data.data is an array
                if (response.data && Array.isArray(response.data.data)) {
                    setStates(response.data.data);
                } else {
                    console.error("Expected an array for states, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load states: Invalid data format');
                }
                // Reset state and city selections when country changes
                setCompanyData(prev => ({
                    ...prev,
                    state_id: '',
                    city_id: ''
                }));
                setCities([]);
            } catch (error) {
                console.error("Error fetching states:", error);
                showPopup('error', 'Error', 'Failed to load states');
            } finally {
                setLoadingLocations(prev => ({ ...prev, states: false }));
            }
        };

        fetchStates();
    }, [companyData.country_id]);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!companyData.state_id) {
                setCities([]);
                return;
            }
            setLoadingLocations(prev => ({ ...prev, cities: true }));
            try {
                const response = await getCities(companyData.state_id);
                if (response.data && Array.isArray(response.data.data)) {
                    setCities(response.data.data);
                } else {
                    console.error("Expected an array for cities, but got:", response.data);
                    showPopup('error', 'Error', 'Failed to load cities: Invalid data format');
                }
                // Reset city selection when state changes
                setCompanyData(prev => ({
                    ...prev,
                    city_id: ''
                }));
            } catch (error) {
                console.error("Error fetching cities:", error);
                showPopup('error', 'Error', 'Failed to load cities');
            } finally {
                setLoadingLocations(prev => ({ ...prev, cities: false }));
            }
        };

        fetchCities();
    }, [companyData.state_id]);

    const validationRules = {
        name: {
            required: true,
            message: 'Name is required'
        },
        uen_number: {
            required: true,
            message: 'UEN number is required'
        },
        contact_number: {
            required: true,
            message: 'Contact number is required'
        },
        email: {
            required: true,
            message: 'Email is required'
        },
        address: {
            required: true,
            message: 'Address is required'
        },
        country_id: {
            required: true,
            message: 'Country is required'
        },
        state_id: {
            required: true,
            message: 'State is required'
        },
        city_id: {
            required: true,
            message: 'City is required'
        },
        contact_name: {
            required: true,
            message: 'Contact name is required'
        },
        contact_email: {
            required: true,
            message: 'Contact email is required'
        },
        contact_phone: {
            required: true,
            message: 'Contact phone number is required'
        },
        remark: {
            required: true,
            message: 'Remark is required'
        }
    };

    const validationRulesWarehouse = {
        warehouse_id: {
            required: true,
            message: 'Warehouse location is required'
        },
        membership_till: {
            required: true,
            message: 'Membership till is required'
        },
        lot_min: {
            required: true,
            message: 'Lot min is required'
        },
        lot_max: {
            required: true,
            message: 'Lot max is required'
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const validateCompanyData = () => {
        const errors = {};

        // Phone number validation regex for Indian numbers
        const phoneRegex = /^[6-9]\d{9}$/; // Indian mobile numbers start with 6-9 and have 10 digits

        if (!companyData.name) errors.name = 'Company name is required';
        if (!companyData.uen_number) errors.uen_number = 'UEN number is required';

        // Company phone number validation
        if (!companyData.contact_number) {
            errors.contact_number = 'Phone number is required';
        } else if (!/^\d+$/.test(companyData.contact_number)) {
            errors.contact_number = 'Phone number must contain only digits';
        } else if (companyData.contact_number.length !== 10) {
            errors.contact_number = 'Phone number must be exactly 10 digits';
        } else if (!phoneRegex.test(companyData.contact_number)) {
            errors.contact_number = 'Phone number must start with 6, 7, 8, or 9';
        }

        if (!companyData.email) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
            errors.email = 'Invalid email format';
        }

        if (!companyData.address) errors.address = 'Address is required';
        if (!companyData.country_id) errors.country_id = 'Country is required';
        if (!companyData.state_id) errors.state_id = 'State is required';
        if (!companyData.city_id) errors.city_id = 'City is required';
        if (!companyData.contact_name) errors.contact_name = 'Contact name is required';

        if (!companyData.contact_email) {
            errors.contact_email = 'Contact email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.contact_email)) {
            errors.contact_email = 'Invalid contact email format';
        }

        // Contact person phone validation
        if (!companyData.contact_phone) {
            errors.contact_phone = 'Contact person phone number is required';
        } else if (!/^\d+$/.test(companyData.contact_phone)) {
            errors.contact_phone = 'Contact phone number must contain only digits';
        } else if (companyData.contact_phone.length !== 10) {
            errors.contact_phone = 'Contact phone number must be exactly 10 digits';
        } else if (!phoneRegex.test(companyData.contact_phone)) {
            errors.contact_phone = 'Contact phone number must start with 6, 7, 8, or 9';
        }

        return errors;
    };

    const validateWarehouseData = () => {
        const errors = {};
        warehouseData.forEach((warehouse, index) => {
            if (!warehouse.warehouse_id) errors[`warehouse_id_${index}`] = 'Warehouse location is required';
            if (!warehouse.membership_till) errors[`membership_till_${index}`] = 'Membership till is required';
            if (!warehouse.lot_min) errors[`lot_min_${index}`] = 'Lot min is required';
            if (!warehouse.lot_max) errors[`lot_max_${index}`] = 'Lot max is required';
        });
        return errors;
    };

    const handleCompanyInputChange = (e) => {
        const { name, value } = e.target;

        // Handle phone number fields - only allow digits and limit to 10 characters
        if (name === 'contact_number' || name === 'contact_phone') {
            // Remove any non-digit characters
            const digitsOnly = value.replace(/\D/g, '');

            // Limit to 10 digits
            const limitedValue = digitsOnly.slice(0, 10);

            setCompanyData(prev => ({
                ...prev,
                [name]: limitedValue
            }));

            // Update input color
            const selectElement = e.target;
            if (limitedValue) {
                selectElement.style.color = '#000000';
            } else {
                selectElement.style.color = '#797775';
            }

            // Clear error when user starts typing
            if (errors[name]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }

            return;
        }

        // Handle other fields normally
        setCompanyData(prev => ({
            ...prev,
            [name]: value
        }));

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };


    const handleNext = async () => {
        const validationErrors = validateCompanyData();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setLoading(true);
        try {
            const companyResponse = await createCompany({
                company_name: companyData.name,
                company_uen_number: companyData.uen_number,
                email: companyData.email,
                contact_number: companyData.contact_number,
                address: companyData.address,
                country_id: companyData.country_id,
                state_id: companyData.state_id,
                city_id: companyData.city_id,
                main_contact_person_name: companyData.contact_name,
                main_contact_person_email: companyData.contact_email,
                main_contact_person_phone_number: companyData.contact_phone,
                remark: companyData.remark,
                status: 1,
            });

            console.log('Full company response:', companyResponse);
            // Check for both 200 and 201 status codes, and also check if response is successful
            if (companyResponse.status === 200 || companyResponse.status === 201) {
                console.log('Company created successfully:', companyResponse.data);

                // Make sure we're getting the correct ID from the response
                const companyId = companyResponse.data.id || companyResponse.data.company_id || companyResponse.data.data?.id;

                if (companyId) {
                    setCompanyId(companyId);
                    setActiveTab('warehouse');
                    showPopup('success', 'Success', 'Company details saved successfully');
                } else {
                    console.error('Company ID not found in response:', companyResponse.data);
                    showPopup('error', 'Error', 'Company created but ID not found');
                }
            } else {
                showPopup('error', 'Error', 'Unexpected response status: ' + companyResponse.status);
            }
        } catch (error) {
            console.error('Error creating company:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create company';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     const companyValidationErrors = validateCompanyData();
    //     if (Object.keys(companyValidationErrors).length > 0) {
    //         setErrors(companyValidationErrors);
    //         showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
    //         return;
    //     }
    //     const warehouseValidationErrors = validateWarehouseData();
    //     if (Object.keys(warehouseValidationErrors).length > 0) {
    //         setErrors(warehouseValidationErrors);
    //         showPopup('error', 'Validation Error', 'Please fill all required warehouse fields');
    //         return;
    //     }
    //     setLoading(true);
    //     try {
    //         // Create company first
    //         const companyResponse = await createCompany({
    //             ...companyData,
    //             contact_person: {
    //                 name: companyData.contact_name,
    //                 email: companyData.contact_email,
    //                 phone_number: companyData.contact_person_phone,
    //                 remark: companyData.remark
    //             }
    //         });

    //         if (companyResponse.status === 200) {
    //             // Create warehouse with company ID
    //             const warehouseResponse = await createCompanyWarehouse({
    //                 company_id: companyResponse.data.id,
    //                 ...warehouseData
    //             });

    //             if (warehouseResponse.status === 200) {
    //                 showPopup('success', 'Success', 'Company and warehouse created successfully');
    //                 await new Promise(resolve => setTimeout(resolve, 1000));
    //                 navigate("/company");
    //             }
    //         }

    //     } catch (error) {
    //         const errorMessage = error.response?.data?.message || 'Failed to create company and warehouse';
    //         showPopup('error', 'Error', errorMessage);
    //         if (error.response?.data?.errors) {
    //             setErrors(error.response.data.errors);
    //         }
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleWarehouseInputChange = (e, index) => {
        const { name, value } = e.target;
        setWarehouseData(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [name]: value
            };
            return updated;
        });

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#c9c9c9';
        }
    };

    useEffect(() => {
        // Maintain select field colors for company form
        const companySelects = document.querySelectorAll('[name="country_id"], [name="state_id"], [name="city_id"]');
        companySelects.forEach(select => {
            if (select.value) {
                select.style.color = '#000000';
            } else {
                select.style.color = '#797775';
            }
        });

        // Maintain select field colors for warehouse forms
        const warehouseSelects = document.querySelectorAll('[name="warehouse_id"]');
        warehouseSelects.forEach(select => {
            if (select.value) {
                select.style.color = '#000000';
            } else {
                select.style.color = '#c9c9c9';
            }
        });
    }, [activeTab, companyData, warehouseData]);

    const handlePrimaryChange = (index) => {
        const updatedWarehouses = warehouseData.map((warehouse, i) => ({
            ...warehouse,
            is_primary: i === index
        }));
        setWarehouseData(updatedWarehouses);
    };

    const addNewWarehouse = () => {
        setWarehouseData(prev => [
            ...prev,
            {
                warehouse_id: '',
                membership_till: '',
                lot_min: '',
                lot_max: '',
                is_primary: false
            }
        ]);
    };

    const removeWarehouse = (index) => {
        if (warehouseData.length === 1) return;

        setWarehouseData(prev => {
            const updated = prev.filter((_, i) => i !== index);
            if (prev[index].is_primary && updated.length > 0) {
                updated[0].is_primary = true;
            }
            return updated;
        });
    };

    const handleSubmitWarehouse = async (e) => {
        e.preventDefault();

        if (!companyId) {
            showPopup('error', 'Error', 'Company ID not found. Please go back and complete company details first.');
            setActiveTab('company');
            return;
        }

        const validationErrors = validateWarehouseData();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showPopup('error', 'Validation Error', 'Please fill all required warehouse fields');
            return;
        }

        setLoading(true);
        try {
            const apiData = {
                company_id: companyId,
                company_warehouses: warehouseData.map(warehouse => ({
                    warehouse_id: warehouse.warehouse_id,
                    membership_till: warehouse.membership_till,
                    min_lot: warehouse.lot_min,
                    max_lot: warehouse.lot_max,
                    is_primary: warehouse.is_primary
                }))
            };

            console.log('Submitting warehouse data:', apiData);
            const response = await createCompanyWarehouse(apiData);

            if (response.status === 200 || response.status === 201) {
                showPopup('success', 'Success', 'Company and warehouses created successfully');
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/company");
            } else {
                showPopup('error', 'Error', 'Unexpected response status: ' + response.status);
            }
        } catch (error) {
            console.error('Error creating warehouses:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create warehouses';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    // const handleSubmit = async () => {
    //     const warehouseValidationErrors = validateWarehouseData();
    //     if (Object.keys(warehouseValidationErrors).length > 0) {
    //         setErrors(warehouseValidationErrors);
    //         showPopup('error', 'Validation Error', 'Please fill all required warehouse fields');
    //         return;
    //     }

    //     setLoading(true);
    //     try {
    //         // Create company first
    //         const companyResponse = await createCompany({
    //             ...companyData,
    //             contact_person: {
    //                 name: companyData.contact_name,
    //                 email: companyData.contact_email,
    //                 phone_number: companyData.contact_person_phone,
    //                 remark: companyData.remark
    //             }
    //         });

    //         if (companyResponse.status === 200) {
    //             // Create warehouse with company ID
    //             const warehouseResponse = await createCompanyWarehouse({
    //                 company_id: companyResponse.data.id,
    //                 ...warehouseData
    //             });

    //             if (warehouseResponse.status === 200) {
    //                 showPopup('success', 'Success', 'Company and warehouse created successfully');
    //                 await new Promise(resolve => setTimeout(resolve, 1000));
    //                 navigate("/company");
    //             }
    //         }
    //     } catch (error) {
    //         const errorMessage = error.response?.data?.message || 'Failed to create company and warehouse';
    //         showPopup('error', 'Error', errorMessage);
    //         if (error.response?.data?.errors) {
    //             setErrors(error.response.data.errors);
    //         }
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    return (
        <div className={`${Styles.company_add}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Create Company</h2>
            </div>

            <div className={`${Styles.company_form}`}>
                <div className={`${Styles.handle_heading}`}>
                    <div className={`${Styles.handle_form}`}>
                        <div className={`${Styles.tab}`}>
                            <h4 onClick={() => handleTabChange('company')} className={`${activeTab === 'company' ? Styles.active : ''}`}>Company details</h4>
                            {activeTab === 'company' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                        <div className={`${Styles.tab}`}>
                            <h4
                                onClick={() => handleTabChange('warehouse')}
                                className={`${activeTab === 'warehouse' ? Styles.active : ''} ${!companyId ? Styles.disabled : ''}`}
                                style={{
                                    cursor: !companyId ? 'not-allowed' : 'pointer',
                                    opacity: !companyId ? 0.5 : 1
                                }}
                            >
                                Warehouse details
                            </h4>
                            {activeTab === 'warehouse' && <hr className={`${Styles.tabBorder}`} />}
                        </div>
                    </div>
                    <hr />
                </div>

                {activeTab === 'company' && (
                    <div className={`${Styles.company_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Company details</h4>
                            <hr />
                        </div>
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input} ${errors.name ? Styles.error : ''}`}>
                                <label>Company name<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="name"
                                        value={companyData.name}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Company Name"
                                        className={`${errors.name ? Styles.errorInput : ''}`}
                                    />
                                    {errors.name && <span className={`${Styles.errorMessage}`}>{errors.name}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.uen_number ? Styles.error : ''}`}>
                                <label>Company UEN number</label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="uen_number"
                                        value={companyData.uen_number}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter UEN Number "
                                        className={`${errors.uen_number ? Styles.errorInput : ''}`}
                                    />
                                    {errors.uen_number && <span className={`${Styles.errorMessage}`}>{errors.uen_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.contact_number ? Styles.error : ''}`}>
                                <label>Phone number<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="tel"
                                        name="contact_number"
                                        value={companyData.contact_number}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Phone Number"
                                        className={`${errors.contact_number ? Styles.errorInput : ''}`}
                                        maxLength="10"
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                    />
                                    {errors.contact_number && <span className={`${Styles.errorMessage}`}>{errors.contact_number}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.email ? Styles.error : ''}`}>
                                <label>Email<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="email"
                                        value={companyData.email}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Email"
                                        className={`${errors.email ? Styles.errorInput : ''}`}
                                    />
                                    {errors.email && <span className={`${Styles.errorMessage}`}>{errors.email}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                <label>Address<span>*</span></label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="address"
                                        value={companyData.address}
                                        onChange={handleCompanyInputChange}
                                        placeholder="Enter Address"
                                        className={`${errors.address ? Styles.errorInput : ''}`}
                                    />
                                    {errors.address && <span className={`${Styles.errorMessage}`}>{errors.address}</span>}
                                </div>
                            </div>
                            <div className={`${Styles.label_input} ${errors.country_id || errors.state_id || errors.city_id ? Styles.error : ''}`}>
                                <label>Country /State /City<span>*</span></label>
                                <div className={`${Styles.select_container}`}>
                                    <div className={`${Styles.country}`}>
                                        <select name='country_id' value={companyData.country_id} onChange={handleCompanyInputChange} className={`${errors.country_id ? Styles.errorInput : ''}`} disabled={loadingLocations.countries}>
                                            <option value="" hidden>{loadingLocations.countries ? 'Loading...' : 'Select Country'}</option>
                                            {countries.map(country => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.country_id && <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>}
                                    </div>
                                    <div className={`${Styles.state}`}>
                                        <select name='state_id' value={companyData.state_id} onChange={handleCompanyInputChange} className={`${errors.state_id ? Styles.errorInput : ''}`} disabled={!companyData.country_id || loadingLocations.states}>
                                            <option value="" hidden>{loadingLocations.states ? 'Loading...' : 'Select State'}</option>
                                            {states.map(state => (
                                                <option key={state.id} value={state.id}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.state_id && <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>}
                                    </div>
                                    <div className={`${Styles.city}`}>
                                        <select name='city_id' value={companyData.city_id} onChange={handleCompanyInputChange} className={`${errors.city_id ? Styles.errorInput : ''}`} disabled={!companyData.state_id || loadingLocations.cities}>
                                            <option value="" hidden>{loadingLocations.cities ? 'Loading...' : 'Select City'}</option>
                                            {cities.map(city => (
                                                <option key={city.id} value={city.id}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.city_id && <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.contact_person}`}>
                            <div className={`${Styles.title2}`}>
                                <h1>Main contact person</h1>
                                <hr />
                            </div>
                            <div className={`${Styles.form}`}>
                                <div className={`${Styles.label_input} ${errors.contact_name ? Styles.error : ''}`}>
                                    <label>Contact name person<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="contact_name"
                                            value={companyData.contact_name}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Name Parson"
                                            className={`${errors.contact_name ? Styles.errorInput : ''}`}
                                        />
                                        {errors.contact_name && <span className={`${Styles.errorMessage}`}>{errors.contact_name}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.contact_email ? Styles.error : ''}`}>
                                    <label>Email<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="contact_email"
                                            value={companyData.contact_email}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Email"
                                            className={`${errors.contact_email ? Styles.errorInput : ''}`}
                                        />
                                        {errors.contact_email && <span className={`${Styles.errorMessage}`}>{errors.contact_email}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.contact_phone ? Styles.error : ''}`}>
                                    <label>Phone number<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="tel"
                                            name="contact_phone"
                                            value={companyData.contact_phone}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Phone Number"
                                            className={`${errors.contact_phone ? Styles.errorInput : ''}`}
                                            maxLength="10"
                                            pattern="[0-9]*"
                                            inputMode="numeric"
                                        />
                                        {errors.contact_phone && <span className={`${Styles.errorMessage}`}>{errors.contact_phone}</span>}
                                    </div>
                                </div>
                                <div className={`${Styles.label_input} ${errors.remark ? Styles.error : ''}`}>
                                    <label>Remark</label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="text"
                                            name="remark"
                                            value={companyData.remark}
                                            onChange={handleCompanyInputChange}
                                            placeholder="Enter Remark"
                                            className={`${errors.remark ? Styles.errorInput : ''}`}
                                        />
                                        {errors.remark && <span className={`${Styles.errorMessage}`}>{errors.remark}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* <div className={`${Styles.buttons}`}>
                            {activeTab === 'company' ? (
                                <button
                                    onClick={handleNext}
                                    disabled={loading}
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className={loading ? Styles.loading : ''}
                                >
                                    {loading ? <Loader size="small" color="white" /> : 'Create'}
                                </button>
                            )}
                            <button
                                onClick={() => navigate("/company")}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        </div> */}
                        <div className={Styles.buttons}>
                            <button onClick={handleNext} disabled={loading}>{loading ? <AiOutlineLoading /> : "Next"}</button>
                            <button onClick={() => navigate("/company")} disabled={loading}>Cancel</button>
                        </div>
                    </div>
                )}

                {activeTab === 'warehouse' && (
                    <div className={`${Styles.warehouse_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Warehouse details</h4>
                            <hr />
                        </div>

                        {/* Warehouse Cards Display */}
                        <div className={`${Styles.warehouse_cards}`}>
                            {warehouseData.map((warehouse, index) => {
                                const selectedWarehouse = warehouses.find(w => w.id == warehouse.warehouse_id);
                                return (
                                    <div
                                        key={index}
                                        className={`${Styles.warehouse_card} ${warehouse.is_primary ? Styles.selected : ''}`}
                                        onClick={() => handlePrimaryChange(index)}
                                    >
                                        <div className={`${Styles.card_content}`}>
                                            <div className={`${Styles.location}`}>
                                                {selectedWarehouse ? selectedWarehouse.name : 'Select Warehouse'} (warehouse name)
                                            </div>
                                            <div className={`${Styles.lot}`}>
                                                Lot # {warehouse.lot_min && warehouse.lot_max ? `${warehouse.lot_min}-${warehouse.lot_max}` : 'Not set'}
                                            </div>
                                            <div className={`${Styles.membership}`}>
                                                Membership till {warehouse.membership_till || 'Not set'}
                                            </div>
                                            <div className={`${Styles.actions}`}>
                                                <span className={`${Styles.set_default}`}>
                                                    {warehouse.is_primary ? 'Primary Warehouse' : 'Set as primary'}
                                                </span>
                                                {warehouseData.length > 1 && (
                                                    <span
                                                        className={`${Styles.remove}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeWarehouse(index);
                                                        }}
                                                    >
                                                        Remove
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {warehouse.is_primary && (
                                            <div className={`${Styles.check_icon}`}>
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                    <circle cx="12" cy="12" r="10" stroke="#8148f5" strokeWidth="2" fill="white" />
                                                    <path d="M9 12l2 2l4 -4" stroke="#8148f5" strokeWidth="2" fill="none" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Add New Warehouse Button */}
                        <div className={`${Styles.add_warehouse_btn}`}>
                            <button
                                type="button"
                                onClick={addNewWarehouse}
                                className={`${Styles.add_btn}`}
                            >
                                + Add Another Warehouse
                            </button>
                        </div>

                        {/* Warehouse Forms */}
                        {warehouseData.map((warehouse, index) => (
                            <div key={index} className={`${Styles.form} ${Styles.warehouse_form}`}>
                                <h5>Warehouse {index + 1}</h5>

                                <div className={`${Styles.label_input} ${errors[`membership_till_${index}`] ? Styles.error : ''}`}>
                                    <label>Membership till</label>
                                    <div className={`${Styles.select_field}`}>
                                        <input
                                            type="date"
                                            name="membership_till"
                                            value={warehouse.membership_till}
                                            onChange={(e) => handleWarehouseInputChange(e, index)}
                                            placeholder="Enter membership till"
                                        />
                                        {errors[`membership_till_${index}`] &&
                                            <span className={`${Styles.errorMessage}`}>{errors[`membership_till_${index}`]}</span>
                                        }
                                    </div>
                                </div>

                                <div className={`${Styles.label_input} ${errors[`warehouse_id_${index}`] ? Styles.error : ''}`}>
                                    <label>Warehouse Name<span>*</span></label>
                                    <div className={`${Styles.select_field}`}>
                                        <select
                                            name="warehouse_id"
                                            className={`${Styles.warehouse_select_field} ${errors[`warehouse_id_${index}`] ? Styles.errorInput : ''}`}
                                            value={warehouse.warehouse_id}
                                            onChange={(e) => handleWarehouseInputChange(e, index)}
                                        >
                                            <option value="" hidden>Select Warehouse Name</option>
                                            {warehouses.filter(w => w.status === 1).map(w => (
                                                <option key={w.id} value={w.id}>
                                                    {w.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`warehouse_id_${index}`] &&
                                            <span className={`${Styles.errorMessage}`}>{errors[`warehouse_id_${index}`]}</span>
                                        }
                                    </div>
                                </div>

                                <div className={`${Styles.label_input} ${errors[`lot_min_${index}`] ? Styles.error : ''}`}>
                                    <label>Lot#</label>
                                    <div className={`${Styles.lot}`}>
                                        <div className={`${Styles.lot_field} ${errors[`lot_min_${index}`] ? Styles.errorInput : ''}`}>
                                            <input
                                                className={`${Styles.lot_field} ${errors[`lot_min_${index}`] ? Styles.errorInput : ''}`}
                                                type="number"
                                                name="lot_min"
                                                placeholder="Minimum"
                                                value={warehouse.lot_min}
                                                onChange={(e) => handleWarehouseInputChange(e, index)}
                                            />
                                            {errors[`lot_min_${index}`] &&
                                                <span className={`${Styles.errorMessage}`}>{errors[`lot_min_${index}`]}</span>
                                            }
                                        </div>
                                        <div className={`${Styles.lot_field} ${errors[`lot_max_${index}`] ? Styles.errorInput : ''}`}>
                                            <input
                                                className={`${Styles.lot_field} ${errors[`lot_max_${index}`] ? Styles.errorInput : ''}`}
                                                type="number"
                                                name="lot_max"
                                                placeholder="Maximum"
                                                value={warehouse.lot_max}
                                                onChange={(e) => handleWarehouseInputChange(e, index)}
                                            />
                                            {errors[`lot_max_${index}`] &&
                                                <span className={`${Styles.errorMessage}`}>{errors[`lot_max_${index}`]}</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleSubmitWarehouse} disabled={loading}>
                                {loading ? <AiOutlineLoading /> : "Create"}
                            </button>
                            <button onClick={() => navigate("/company")}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CompanyAdd
